import React from "react";
import { Badge } from "../../../../ui/components/Badge";
import { Table } from "../../../../ui/components/Table";
import type { GlTreeNode } from "../../data/mappers/GlManagementTreeMapper";

function ParentBadge() {
  return (
    <span className="font-['Figtree'] text-[11px] font-[400] leading-[11px] text-subtext-color rounded-full border border-solid border-neutral-border bg-neutral-100 px-1.5 py-0.5">
      Parent
    </span>
  );
}

function GroupBadge() {
  return (
    <span className="font-['Figtree'] text-[11px] font-[400] leading-[11px] text-subtext-color rounded-full border border-solid border-neutral-border bg-neutral-50 px-1.5 py-0.5">
      Group
    </span>
  );
}

function GlNameCell({ node, depth }: { node: GlTreeNode; depth: number }) {
  const isStructural = node.isGroup || node.hasChildren;

  if (isStructural) {
    return (
      <div
        className="flex items-center gap-2"
        style={depth > 0 ? { paddingLeft: depth * 24 } : undefined}
      >
        {depth > 0 && (
          <span className="text-body font-body text-subtext-color">└</span>
        )}
        <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
          {node.glName}
        </span>
        {node.isGroup ? <GroupBadge /> : <ParentBadge />}
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-1.5"
      style={depth > 0 ? { paddingLeft: 24 + (depth - 1) * 24 } : undefined}
    >
      {depth > 0 && (
        <span className="text-body font-body text-subtext-color">└</span>
      )}
      <span className="whitespace-nowrap text-body font-body text-default-font">
        {node.glName}
      </span>
    </div>
  );
}

function StatusCell({ node }: { node: GlTreeNode }) {
  const isUnderReview = node.status === "under_review";
  return isUnderReview ? (
    <Badge variant="warning">Under Review</Badge>
  ) : (
    <Badge variant="success">Active</Badge>
  );
}

interface GlManagementTreeRowProps {
  node: GlTreeNode;
  depth: number;
  parent?: GlTreeNode;
  renderBill: (node: GlTreeNode, parent?: GlTreeNode) => React.ReactNode;
  renderInvoice: (node: GlTreeNode, parent?: GlTreeNode) => React.ReactNode;
  renderActions?: (node: GlTreeNode, parent?: GlTreeNode) => React.ReactNode;
}

function GlManagementTreeRow({
  node,
  depth,
  parent,
  renderBill,
  renderInvoice,
  renderActions,
}: GlManagementTreeRowProps) {
  const isParent = node.isGroup || node.hasChildren;

  return (
    <>
      <Table.Row className={isParent ? "bg-neutral-100" : undefined}>
        <Table.Cell>
          <GlNameCell node={node} depth={depth} />
        </Table.Cell>
        <Table.Cell>
          <span className="whitespace-nowrap text-body font-body text-subtext-color">
            {node.accountTypeLabel}
          </span>
        </Table.Cell>
        <Table.Cell>
          <StatusCell node={node} />
        </Table.Cell>
        <Table.Cell>{renderBill(node, parent)}</Table.Cell>
        <Table.Cell>{renderInvoice(node, parent)}</Table.Cell>
        {renderActions && (
          <Table.Cell>{renderActions(node, parent)}</Table.Cell>
        )}
      </Table.Row>
      {node.children.map((child) => (
        <GlManagementTreeRow
          key={child.id > 0 ? String(child.id) : child.accountId}
          node={child}
          depth={depth + 1}
          parent={node}
          renderBill={renderBill}
          renderInvoice={renderInvoice}
          renderActions={renderActions}
        />
      ))}
    </>
  );
}

export interface GlManagementTreeRowsProps {
  nodes: GlTreeNode[];
  renderBill: (node: GlTreeNode, parent?: GlTreeNode) => React.ReactNode;
  renderInvoice: (node: GlTreeNode, parent?: GlTreeNode) => React.ReactNode;
  renderActions?: (node: GlTreeNode, parent?: GlTreeNode) => React.ReactNode;
  showActionsColumn?: boolean;
}

export function GlManagementTreeTableHeader({
  showActionsColumn = false,
}: {
  showActionsColumn?: boolean;
}) {
  return (
    <Table.HeaderRow>
      <Table.HeaderCell>GL Name</Table.HeaderCell>
      <Table.HeaderCell>Account Type</Table.HeaderCell>
      <Table.HeaderCell>Status</Table.HeaderCell>
      <Table.HeaderCell>Bill</Table.HeaderCell>
      <Table.HeaderCell>Invoice</Table.HeaderCell>
      {showActionsColumn && <Table.HeaderCell>Actions</Table.HeaderCell>}
    </Table.HeaderRow>
  );
}

export function GlManagementTreeRows({
  nodes,
  renderBill,
  renderInvoice,
  renderActions,
}: GlManagementTreeRowsProps) {
  return (
    <>
      {nodes.map((node) => (
        <GlManagementTreeRow
          key={node.id > 0 ? String(node.id) : node.accountId}
          node={node}
          depth={0}
          renderBill={renderBill}
          renderInvoice={renderInvoice}
          renderActions={renderActions}
        />
      ))}
    </>
  );
}

export function renderYesNoFlag(value: boolean) {
  return (
    <span
      className={`text-body font-body ${
        value ? "text-success-600" : "text-subtext-color"
      }`}
    >
      {value ? "Yes" : "No"}
    </span>
  );
}
